import { useGetConsolidatedItemNode } from "./useConsolidatedItemNodes"

import { useGetBundleByCode } from "@hooks/bundles"
import { useGetProductBySku } from "@hooks/products"
import { storyblokLinkToYocoLink } from "@utils/storyblok"
import {
  consolidateBundle,
  consolidateOnlineProduct,
  consolidateProduct,
  parseConsolidatedPrice,
} from "@utils/V2/consolidateItem"
import { isStoryblokPreview } from "@utils/V2/storyblokPreview"
import { useCallback } from "react"

export { parseConsolidatedPrice }

export const isConsolidatedPurchasableItem = (
  item?: YocoCom.ConsolidatedItem | null
): item is YocoCom.ConsolidatedPurchasableItem =>
  item?.type === "product" || item?.type === "bundle"

export type CmsOnlyItemFields = Pick<
  YocoCom.ConsolidatedPurchasableItemBase,
  | "name"
  | "ctaText"
  | "ctaLink"
  | "priceSideScript"
  | "priceSubScript"
  | "waitlistLink"
  | "paymentStripDark"
  | "paymentStripLight"
>

export const getCmsOnlyItemFields = (
  item: Storyblok.Product | Storyblok.Bundle
): CmsOnlyItemFields => {
  const config = item.content

  const sharedFields = {
    name: config.name,
    ctaText: config.buyNowText || "Buy Now",
    waitlistLink: storyblokLinkToYocoLink(config.waitlistLink),
    paymentStripDark: config.paymentStripDark,
    paymentStripLight: config.paymentStripLight,
  }

  if (config.component === "product") {
    return {
      ...sharedFields,
      ctaLink: storyblokLinkToYocoLink(config.buyLink),
      priceSideScript: config.priceSuffixLabel,
      priceSubScript: config.priceInfoText,
    }
  }

  return {
    ...sharedFields,
    ctaLink: storyblokLinkToYocoLink(config.shopPage),
    priceSideScript: config.priceSidescript,
    priceSubScript: config.priceSubscript,
  }
}

export const useGetConsolidatedItem = () => {
  const getSdkProduct = useGetProductBySku()
  const getSdkBundle = useGetBundleByCode()
  const getConsolidatedItemNode = useGetConsolidatedItemNode()

  const getConsolidatedItem = useCallback(
    (
      item: Storyblok.Product | Storyblok.Bundle | Storyblok.OnlineProduct
    ): YocoCom.ConsolidatedItem | undefined => {
      const config = item.content

      // Hand-wrapped items may have an empty outer uuid; ordinary relations
      // may omit config.uuid.
      const storyblokUuid = config.uuid || item.uuid

      if (config.component === "product") {
        if (!isStoryblokPreview()) {
          const node = getConsolidatedItemNode(storyblokUuid)

          if (node?.type === "product") {
            return node
          }
        }

        const sdkProduct = getSdkProduct(config.sku, config.storefront)

        return sdkProduct
          ? consolidateProduct(config, sdkProduct, storyblokUuid)
          : undefined
      }

      if (config.component === "bundle") {
        if (!isStoryblokPreview()) {
          const node = getConsolidatedItemNode(storyblokUuid)

          if (node?.type === "bundle") {
            return node
          }
        }

        const sdkBundle = getSdkBundle(config.code, config.storefront)

        return sdkBundle
          ? consolidateBundle(config, sdkBundle, storyblokUuid)
          : undefined
      }

      if (!isStoryblokPreview()) {
        const node = getConsolidatedItemNode(storyblokUuid)

        if (node?.type === "onlineProduct") {
          return node
        }
      }

      return consolidateOnlineProduct(config, storyblokUuid)
    },
    [getSdkProduct, getSdkBundle, getConsolidatedItemNode]
  )

  return getConsolidatedItem
}
